import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import usePathname from '@magoosh/utils/use_pathname';

import * as api from './api';
import Launcher from './components/launcher';
import PanelManager from './components/panel_manager';
import { AITutorProvider, useAITutorContext } from './context';
import { Interaction, Skill, TutoringSession } from './types';

const AITutor: React.FC<{}> = (props) => {
  const { currentContext, handleNewInteractions, pushToStack, tutoringSessionsStack } = useAITutorContext();
  const pathname = usePathname();

  // We use these in the launchSkill closure below. Need to use a ref to ensure we have access to
  // the current value
  const tutoringSession = useRef(tutoringSessionsStack?.at(-1));
  const tutoringSessionContext = useRef(null);

  useEffect(() => {
    tutoringSession.current = tutoringSessionsStack?.at(-1);
  }, [tutoringSessionsStack]);

  useEffect(() => {
    tutoringSessionContext.current = currentContext();
  }, [currentContext()]);

  useEffect(() => {
    const launchSkill = (e) => {
      if (tutoringSession.current?.domain?.klass === e.detail.domain.klass) {
        // Immediately add to the interaction list for quick UI feedback
        const provisionalInteraction = {
          content: {},
          createdAt: moment.utc().valueOf() / 1000,
          finishedAt: null,
          id: uuidv4(),
          parts: [],
          path: pathname,
          provisional: true,
          status: 'waiting',
          tutoringSessionId: tutoringSession.current.id,
          type: 'assistant'
        };
        handleNewInteractions([provisionalInteraction]);

        api.createAITutoringSessionAssistantInteraction({
          currentContext: tutoringSessionContext.current,
          tutoringSessionId: tutoringSession.current.id,
          parts: [
            {
              content: { skill: e.detail.initialSkill.klass },
              type: 'TestPrep::AI::TutoringSessions::InteractionParts::Skill'
            }
          ]
        }).then((interactions) => {
          handleNewInteractions(interactions);
        });
      } else {
        pushToStack({ ...e.detail });
      }
    };

    document.addEventListener('aiTutor.launchSkill', launchSkill);

    return () => {
      document.removeEventListener('aiTutor.launchSkill', launchSkill);
    }
  }, []);

  return (
    <>
      <Launcher />

      <PanelManager>
        <PanelManager.TutoringPanel />
        <PanelManager.ActivityPanel />
      </PanelManager>
    </>
  );
};

interface Props {
  resizeableContainer?: {
    marginLeft: string; // e.g. '57px'
    maxWidth: string;  // e.g. '700px'
  };
  tutoringSessionsStack?: TutoringSession[];
  width?: number;
}

export default (props: Props) => (
  <AITutorProvider initialData={props}>
    <AITutor />
  </AITutorProvider>
);
